/**
 * Document Management Page
 * Admin page for viewing and managing all uploaded documents
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getStatusColor } from '../../utils/statusColors';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import DescriptionIcon from '@mui/icons-material/Description';
import { apiGet, apiPut } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { useNotification } from '../../contexts/NotificationContext';
import { formatDate, formatFileSize } from '../../utils/formatters';
import useDocumentActions from '../../hooks/useDocumentActions';
import SortableHeaderCell from '../../components/common/SortableHeaderCell';
import useTableSort from '../../hooks/useTableSort';
import usePagination from '../../hooks/usePagination';

const PENDING_DOC_COLUMNS = [
  { key: 'file_name',          align: 'left', getValue: (d) => d.file_name },
  { key: 'investor_name',      align: 'left', getValue: (d) => d.investor_name },
  { key: 'contract_reference', align: 'left', getValue: (d) => d.contract_reference },
  { key: 'document_type',      align: 'left', getValue: (d) => d.document_type },
  { key: 'file_size',          align: 'left', getValue: (d) => d.file_size != null ? Number(d.file_size) : null },
  { key: 'created_at',         align: 'left', getValue: (d) => d.created_at },
  { key: 'status',             align: 'left', getValue: (d) => d.status },
];

// Fallback document types (used if API fails to load)
const FALLBACK_DOCUMENT_TYPES = [
  { code: 'kyc', display_name: 'KYC Document' },
  { code: 'proof_of_payment', display_name: 'Proof of Payment' },
  { code: 'bank_statement', display_name: 'Bank Statement' },
  { code: 'contract', display_name: 'Contract Document' },
  { code: 'supporting_doc', display_name: 'Supporting Document' },
  { code: 'other', display_name: 'Other' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'reupload_requested', label: 'Re-upload Requested' },
];

function DocumentManagementPage() {
  const notify = useNotification();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [documentTypes, setDocumentTypes] = useState(FALLBACK_DOCUMENT_TYPES);

  // Document download (shared hook)
  const { downloadDocument } = useDocumentActions();

  // Pagination
  const pagination = usePagination({ defaultRowsPerPage: 25 });
  const { page, rowsPerPage, resetPage } = pagination;
  const [total, setTotal] = useState(0);
  const sort = useTableSort({ onSortChange: resetPage });

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    document_type: '',
    from_date: '',
    to_date: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Review dialog
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewAction, setReviewAction] = useState('approved');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append('limit', rowsPerPage);
      params.append('offset', page * rowsPerPage);

      if (filters.status) params.append('status', filters.status);
      if (filters.document_type) params.append('document_type', filters.document_type);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (sort.apiParams.sort_by) {
        params.append('sort_by', sort.apiParams.sort_by);
        params.append('sort_dir', sort.apiParams.sort_dir);
      }

      // Use different endpoint based on tab
      const endpoint = tabValue === 0
        ? `/admin/documents/pending`
        : `/admin/documents/all?${params.toString()}`;

      const response = await apiGet(endpoint);

      if (tabValue === 0) {
        // Pending documents endpoint returns all pending
        setDocuments(response.data || []);
        setTotal(response.count || response.data?.length || 0);
      } else {
        setDocuments(response.data || []);
        setTotal(response.pagination?.total || response.data?.length || 0);
      }
    } catch (err) {
      notify.error('Failed to load documents: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify is stable
  }, [tabValue, page, rowsPerPage, filters, sort.apiParams]);

  const loadStats = useCallback(async () => {
    try {
      const response = await apiGet('/admin/documents/stats/summary');
      setStats(response.data);
    } catch (err) {
      logger.error('Failed to load document stats', { message: err.message, status: err.response?.status });
    }
  }, []);

  const loadDocumentTypes = useCallback(async () => {
    try {
      const response = await apiGet('/refdata/document-types/active');
      if (response.data && response.data.length > 0) {
        setDocumentTypes(response.data);
      }
    } catch (err) {
      logger.warn('Failed to load document types, using fallback', { message: err.message, status: err.response?.status });
    }
  }, []);

  useEffect(() => {
    loadDocumentTypes();
    loadDocuments();
    loadStats();
  }, [loadDocumentTypes, loadDocuments, loadStats]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    resetPage();
  };

  const handleReviewClick = (doc, action) => {
    setReviewTarget(doc);
    setReviewAction(action);
    setReviewNotes('');
    setReviewDialog(true);
  };

  const handleReviewSubmit = async () => {
    if (!reviewTarget) return;

    try {
      setReviewing(true);
      await apiPut(`/admin/documents/${reviewTarget.id}/review`, {
        status: reviewAction,
        review_notes: reviewNotes,
      });

      notify.success(`Document ${reviewAction} successfully`);
      setReviewDialog(false);
      setReviewTarget(null);
      loadDocuments();
      loadStats();
    } catch (err) {
      notify.error('Failed to review document: ' + (err.message || 'Unknown error'));
    } finally {
      setReviewing(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    resetPage();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      document_type: '',
      from_date: '',
      to_date: '',
    });
    resetPage();
  };


  const getDocumentTypeLabel = (code) => {
    const found = documentTypes.find(t => t.code === code);
    return found?.display_name || code;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Document Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Toggle Filters">
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterListIcon color={showFilters ? 'primary' : 'inherit'} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={() => { loadDocuments(); loadStats(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="warning.main">{stats.pending}</Typography>
                <Typography variant="body2" color="text.secondary">Pending Review</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main">{stats.approved}</Typography>
                <Typography variant="body2" color="text.secondary">Approved</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error.main">{stats.rejected}</Typography>
                <Typography variant="body2" color="text.secondary">Rejected</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4">{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">Total Documents</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={`Pending Review (${stats?.pending || 0})`} />
          <Tab label="All Documents" />
        </Tabs>

        {/* Filters (only for All Documents tab) */}
        {showFilters && tabValue === 1 && (
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid
                size={{
                  xs: 12,
                  sm: 3
                }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 3
                }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={filters.document_type}
                    label="Document Type"
                    onChange={(e) => handleFilterChange('document_type', e.target.value)}
                  >
                    <MenuItem value="">All Types</MenuItem>
                    {documentTypes.map(type => (
                      <MenuItem key={type.code} value={type.code}>{type.display_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 2
                }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From Date"
                  value={filters.from_date}
                  onChange={(e) => handleFilterChange('from_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 2
                }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To Date"
                  value={filters.to_date}
                  onChange={(e) => handleFilterChange('to_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 2
                }}>
                <Button onClick={clearFilters} size="small">Clear Filters</Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : documents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {tabValue === 0 ? 'No documents pending review' : 'No documents found'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <SortableHeaderCell sort={sort} columnKey="file_name">File Name</SortableHeaderCell>
                    <SortableHeaderCell sort={sort} columnKey="investor_name">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={sort} columnKey="contract_reference">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={sort} columnKey="document_type">Type</SortableHeaderCell>
                    <SortableHeaderCell sort={sort} columnKey="file_size">Size</SortableHeaderCell>
                    <SortableHeaderCell sort={sort} columnKey="created_at">Uploaded</SortableHeaderCell>
                    <SortableHeaderCell sort={sort} columnKey="status">Status</SortableHeaderCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sort.sortRows(documents, PENDING_DOC_COLUMNS).map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {doc.file_name}
                        </Typography>
                        {doc.uploader_notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {doc.uploader_notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{doc.investor_name || '-'}</TableCell>
                      <TableCell>{doc.contract_reference || '-'}</TableCell>
                      <TableCell>{getDocumentTypeLabel(doc.document_type)}</TableCell>
                      <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(doc.created_at)}</Typography>
                        {doc.uploaded_by_email && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {doc.uploaded_by_email}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={doc.status}
                          size="small"
                          color={getStatusColor(doc.status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => downloadDocument(doc.id)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {doc.status === 'pending' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleReviewClick(doc, 'approved')}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleReviewClick(doc, 'rejected')}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {tabValue === 1 && (
              <TablePagination {...pagination.tableProps(total)} />
            )}
          </>
        )}
      </Paper>
      {/* Review Dialog */}
      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {reviewAction === 'approved' ? 'Approve Document' : 'Reject Document'}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            <strong>File:</strong> {reviewTarget?.file_name}
          </Typography>
          <Typography gutterBottom>
            <strong>Type:</strong> {getDocumentTypeLabel(reviewTarget?.document_type)}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={reviewAction === 'approved' ? 'Comments (Optional)' : 'Rejection Reason'}
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
            sx={{ mt: 2 }}
            placeholder={reviewAction === 'rejected' ? 'Please provide a reason for rejection' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={reviewAction === 'approved' ? 'success' : 'error'}
            onClick={handleReviewSubmit}
            disabled={reviewing || (reviewAction === 'rejected' && !reviewNotes)}
          >
            {reviewing ? <CircularProgress size={20} /> : reviewAction === 'approved' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DocumentManagementPage;
